import { useState, useCallback } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQBuilderProps {
  faqs: FAQItem[];
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  onChange: (faqs: FAQItem[]) => void;
}

export default function FAQBuilder({ faqs, enabled, onToggle, onChange }: FAQBuilderProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const addQuestion = () => {
    onChange([...faqs, { question: '', answer: '' }]);
  };

  const removeQuestion = (index: number) => {
    onChange(faqs.filter((_, i) => i !== index));
  };

  const duplicateQuestion = (index: number) => {
    const newFaqs = [...faqs];
    newFaqs.splice(index + 1, 0, { ...faqs[index] });
    onChange(newFaqs);
  };

  const updateQuestion = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    onChange(newFaqs);
  };

  const moveQuestion = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= faqs.length) return;
    const newFaqs = [...faqs];
    const [removed] = newFaqs.splice(fromIndex, 1);
    newFaqs.splice(toIndex, 0, removed);
    onChange(newFaqs);
    setDragIndex(null);
  }, [faqs, onChange]);

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-foreground-900">FAQ Section</h3>
          <p className="text-xs text-foreground-500 mt-0.5">Add frequently asked questions to this post</p>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
            enabled ? 'bg-primary-500' : 'bg-background-300'
          }`}
          role="switch"
          aria-checked={enabled}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {enabled && (
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIndex !== null && dragIndex !== idx) {
                  moveQuestion(dragIndex, idx);
                }
              }}
              className={`bg-background-50 border border-background-200 rounded-xl overflow-hidden transition-colors ${
                dragIndex === idx ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center justify-between px-4 py-2.5 bg-background-100 border-b border-background-200">
                <div className="flex items-center gap-2">
                  <i className="ri-draggable text-foreground-400 cursor-grab text-sm" title="Drag to reorder"></i>
                  <span className="text-xs font-semibold text-foreground-600">
                    Question {idx + 1}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => duplicateQuestion(idx)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
                    title="Duplicate"
                  >
                    <i className="ri-file-copy-line text-xs"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeQuestion(idx)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={faq.question}
                    onChange={(e) => updateQuestion(idx, 'question', e.target.value)}
                    placeholder="Enter the question..."
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">
                    Answer
                  </label>
                  <textarea
                    value={faq.answer}
                    onChange={(e) => updateQuestion(idx, 'answer', e.target.value)}
                    placeholder="Enter the answer... (supports HTML)"
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition resize-none"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addQuestion}
            className="w-full py-2.5 border-2 border-dashed border-background-300 hover:border-primary-300 hover:bg-primary-50 rounded-xl text-sm font-semibold text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-add-line"></i>
            Add Question
          </button>
        </div>
      )}
    </div>
  );
}